import { useQuery } from '@tanstack/react-query'
import { apiGet, apiPost } from '@/lib/api'
import { queryClient } from '@/lib/queryClient'

// ── Types ────────────────────────────────────────────────────────────────────

export interface AuthUser {
  id: string
  email: string
  name: string
  role: string
}

// ── Hook ─────────────────────────────────────────────────────────────────────

/**
 * useAuth — session-aware hook backed by TanStack Query.
 *
 * Calls GET /api/v1/me to retrieve the authenticated user.
 * On 401 the query enters error state — this is the normal
 * "not logged in" signal, NOT a transient failure.
 *
 * The httpOnly cookie is sent automatically via credentials: 'include'
 * (configured in apiGet).
 */
export function useAuth() {
  const { data: user, isLoading, isError } = useQuery<AuthUser>({
    queryKey: ['auth', 'me'],
    queryFn: () => apiGet<AuthUser>('/api/v1/me'),
    retry: false, // 401 means unauthenticated — do not retry
    staleTime: 1000 * 60 * 5, // 5 min
    refetchOnWindowFocus: true, // re-check session when user returns to tab
  })

  return {
    user: user ?? null,
    isLoading,
    isAuthenticated: !!user && !isLoading,
    error: isError,
  }
}

// ── Login helpers ────────────────────────────────────────────────────────────

/** Full-page redirect to Express Google OAuth initiation */
export function loginWithGoogle() {
  window.location.href = '/auth/google'
}

/** Full-page redirect to Express Microsoft OAuth initiation */
export function loginWithMicrosoft() {
  window.location.href = '/auth/microsoft'
}

// ── Logout ───────────────────────────────────────────────────────────────────

/**
 * POST /auth/logout to destroy the server-side session,
 * immediately clear the cached user in TanStack Query,
 * then redirect to the login page.
 */
export async function logout() {
  await apiPost('/auth/logout', {})
  queryClient.setQueryData(['auth', 'me'], null)
  window.location.href = '/login'
}
